// @djsem/ui — shell stylesheet (injected once by AppShell).
//
// Inline styles can't express :hover / :focus-visible / scrollbars, so the shell injects this
// small stylesheet under a `djsem-` class namespace. Values come from tokens.ts so the two
// sources can't drift apart. Rendered exactly once (guarded by id).

import { tokens } from "./tokens";

/** DOM id used to guard against injecting the shell stylesheet more than once. */
export const SHELL_STYLE_ID = "djsem-shell-styles";

/** The shell's injected CSS (hover/focus-visible/scrollbar states inline styles can't express). */
export const shellStylesheet = `
/* ---- base ---- */
.djsem-shell { color-scheme: dark; }
.djsem-shell *::selection { background: ${tokens.color.accentDim}; color: ${tokens.color.text}; }
.djsem-shell ::-webkit-scrollbar { width: 10px; height: 10px; }
.djsem-shell ::-webkit-scrollbar-thumb { background: ${tokens.color.borderHi}; border-radius: 5px; border: 2px solid ${tokens.color.bg}; }
.djsem-shell ::-webkit-scrollbar-track { background: transparent; }

/* ---- keyboard focus (a11y, prompt 89) ---- */
.djsem-shell :where(button, input, select, a, [tabindex]):focus-visible {
  outline: 2px solid ${tokens.color.accent};
  outline-offset: 2px;
  border-radius: ${tokens.radius.sm};
}

/* ---- nav ---- */
.djsem-nav-group { margin: 14px 8px 4px; font-size: 10px; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; color: ${tokens.color.muted}; user-select: none; }
.djsem-nav-group:first-child { margin-top: 6px; }
.djsem-nav-item {
  display: flex; width: 100%; align-items: center; gap: 10px;
  background: none; color: ${tokens.color.textDim};
  border: none; border-left: 2px solid transparent; border-radius: 0 ${tokens.radius.sm} ${tokens.radius.sm} 0;
  padding: 7px 10px 7px 11px; font-size: ${tokens.text.base}px; font-family: inherit;
  cursor: pointer; text-align: left;
  transition: background 140ms ${tokens.ease}, color 140ms ${tokens.ease}, border-color 140ms ${tokens.ease};
}
.djsem-nav-icon { display: inline-flex; flex: 0 0 auto; color: ${tokens.color.muted}; transition: color 140ms ${tokens.ease}; }
.djsem-nav-label { flex: 1 1 auto; }
.djsem-nav-item:hover { background: ${tokens.color.surface2}; color: ${tokens.color.text}; }
.djsem-nav-item:hover .djsem-nav-icon { color: ${tokens.color.textDim}; }
.djsem-nav-item[aria-current="page"] {
  background: linear-gradient(90deg, ${tokens.color.surface2}, transparent 140%);
  color: ${tokens.color.text};
  border-left-color: ${tokens.color.accent};
  font-weight: 500;
}
.djsem-nav-item[aria-current="page"] .djsem-nav-icon { color: ${tokens.color.accent}; }
.djsem-nav-item.is-locked { color: ${tokens.color.muted}; }
.djsem-nav-item.is-locked:hover { color: ${tokens.color.textDim}; }

/* ---- top bar ---- */
.djsem-topbtn {
  position: relative;
  display: inline-flex; align-items: center; gap: 6px;
  background: none; color: ${tokens.color.textDim};
  border: 1px solid transparent; border-radius: ${tokens.radius.sm};
  padding: 4px 8px; font-size: ${tokens.text.sm}px; font-family: inherit; cursor: pointer;
  transition: border-color 140ms ${tokens.ease}, color 140ms ${tokens.ease}, background 140ms ${tokens.ease};
}
.djsem-topbtn:hover { border-color: ${tokens.color.borderHi}; color: ${tokens.color.text}; background: ${tokens.color.surface2}; }
.djsem-topicon { display: inline-flex; }
.djsem-topcount {
  position: absolute; top: -3px; right: -3px;
  min-width: 15px; height: 15px; padding: 0 3px;
  display: inline-flex; align-items: center; justify-content: center;
  background: ${tokens.color.accent}; color: #12100a;
  font-size: 9px; font-weight: 700; border-radius: 999px;
  box-shadow: 0 0 0 2px ${tokens.color.surface};
}
.djsem-search {
  flex: 1; max-width: 380px;
  background: ${tokens.color.bg}; color: ${tokens.color.textDim};
  border: 1px solid ${tokens.color.border}; border-radius: ${tokens.radius.sm};
  padding: 5px 11px; font-size: ${tokens.text.sm}px; font-family: inherit;
  cursor: pointer; transition: border-color 140ms ${tokens.ease}, background 140ms ${tokens.ease};
}
.djsem-search:hover { border-color: ${tokens.color.borderHi}; background: ${tokens.color.surface}; }
.djsem-search:focus { outline: none; border-color: ${tokens.color.accentDim}; }

/* ---- command palette ---- */
.djsem-palette-item {
  display: flex; width: 100%; justify-content: space-between; align-items: center; gap: 12px;
  background: none; border: none; color: ${tokens.color.text};
  padding: 9px 14px; font-size: ${tokens.text.base}px; font-family: inherit;
  cursor: pointer; text-align: left;
  transition: background 100ms ${tokens.ease};
}
.djsem-palette-item:hover { background: ${tokens.color.surface2}; }
.djsem-palette-item.is-locked { color: ${tokens.color.muted}; }

/* ---- generic button ---- */
/* Task #22: pill shape (radius.full), matching Djoid's rounded-full CTA convention — the
   most recognizable single visual signature in the reference. Fill stays DJSEM's own
   champagne gold rather than Djoid's neutral white, per tokens.ts's "inspired by, not a
   reskin" note. */
.djsem-btn {
  display: inline-flex; align-items: center; gap: 8px;
  background: none; color: ${tokens.color.text};
  border: 1px solid ${tokens.color.borderHi}; border-radius: ${tokens.radius.full};
  padding: 6px 16px; font-size: ${tokens.text.sm}px; font-family: inherit; cursor: pointer;
  transition: border-color 160ms ${tokens.ease}, color 160ms ${tokens.ease}, background 160ms ${tokens.ease};
}
.djsem-btn:hover { border-color: ${tokens.color.accent}; color: ${tokens.color.accentHi}; }
.djsem-btn--primary { background: ${tokens.color.accent}; border-color: ${tokens.color.accent}; color: #12100a; font-weight: 600; }
.djsem-btn--primary:hover { background: ${tokens.color.accentHi}; border-color: ${tokens.color.accentHi}; color: #12100a; }
.djsem-btn:disabled { opacity: 0.45; cursor: not-allowed; }

/* ---- panel hover lift ---- */
.djsem-panel { transition: border-color 200ms ${tokens.ease}; }
.djsem-panel:hover { border-color: ${tokens.color.borderHi}; }

/* ---- reduced motion ---- */
@media (prefers-reduced-motion: reduce) {
  .djsem-shell *, .djsem-shell *::before, .djsem-shell *::after {
    transition-duration: 0.01ms !important; animation-duration: 0.01ms !important;
  }
}
`;

/** Idempotent injection — safe to call from every AppShell mount. */
export function ensureShellStyles(): void {
  if (typeof document === "undefined") return; // SSR-safe
  if (document.getElementById(SHELL_STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = SHELL_STYLE_ID;
  el.textContent = shellStylesheet;
  document.head.appendChild(el);
}
